import clsx from "clsx";
import { FC, Suspense, useEffect, useState } from "react";
import { useFragment, useLazyLoadQuery } from "react-relay";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { graphql } from "relay-runtime";
import { assetPairFilter, priceFormatter } from "rujira.js";
import {
  AssetLabel,
  createSortContext,
  Decimal,
  Fiat,
  i18n,
  IconDenom,
  Icons,
  Input,
  nFormatter,
  SortDir,
  useQueryParam,
} from "rujira.ui";
import { MinusCircle } from "rujira.ui/src/components/icons/Icons";
import { useFavorites } from "../../services/favorites";
import { toFinAssetId } from "../Trade";
import { MarketsItemFragment$key } from "./__generated__/MarketsItemFragment.graphql";
import {
  FinSortBy,
  MarketsQuery,
  MarketsQuery$data,
} from "./__generated__/MarketsQuery.graphql";

enum Section {
  Featured = "Featured",
  Usdc = "USDC",
  Btc = "BTC",
  Eth = "ETH",
  Pegged = "Pegged",
  All = "All",
  Favorites = "favorites",
}

const sections: Section[] = [
  Section.Featured,
  //Section.Usdc,
  Section.Btc,
  Section.Eth,
  Section.Pegged,
  Section.All,
  Section.Favorites,
];

const checkFeatured = (base: string): boolean =>
  [
    "AVAX-AVAX",
    "BASE-ETH",
    "BCH-BCH",
    "BSC-BNB",
    "BTC-BTC",
    "DOGE-DOGE",
    "ETH-ETH",
    "GAIA-ATOM",
    "LTC-LTC",
    "THOR.AUTO",
    "THOR.LQDY",
    "THOR.RUJI",
    "THOR.RUNE",
    "THOR.TCY",
    "XRP-XRP",
  ].includes(base);

const checkPegged = (base: string, quote: string): boolean =>
  (base.includes("USD") && quote.includes("USD")) ||
  (base.includes("BTC") && quote.includes("BTC"));

const sectionFilter =
  (favorites: /* Record<string, boolean> */ Array<string>, section?: Section) =>
  (a: {
    id: string;
    address: string;
    assetBase: { asset: string; metadata: { symbol: string } };
    assetQuote: { asset: string; metadata: { symbol: string } };
  }): boolean => {
    if (!section) return true;
    if (section === Section.All) return true;
    if (section === Section.Favorites) return favorites.includes(a.id);
    if (section === Section.Featured) return checkFeatured(a.assetBase.asset);
    if (section === Section.Pegged)
      return checkPegged(
        a.assetBase.metadata.symbol,
        a.assetQuote.metadata.symbol
      );

    return (
      a.assetBase.metadata.symbol === section ||
      a.assetQuote.metadata.symbol === section
    );
  };

const q = graphql`
  query MarketsQuery($sortBy: FinSortBy!, $sortDir: SortDir!) {
    finV2(first: 100, sortBy: $sortBy, sortDir: $sortDir) {
      edges {
        node {
          id
          address
          assetBase {
            asset
            metadata {
              symbol
            }
          }
          assetQuote {
            asset
            metadata {
              symbol
            }
          }
          deploymentStatus
          ...MarketsItemFragment
        }
      }
    }
  }
`;

const sort = createSortContext<FinSortBy>("VOLUME_USD", SortDir.Desc);

export const Markets = ({ closeDrawer }: { closeDrawer?: () => void }) => {
  const [section, setSection] = useQueryParam<Section>("s", Section.Featured);
  const [query, setQuery] = useQueryParam<string>("q", "");
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    if (!searched && section === Section.Featured && query !== "") {
      setSection(Section.All);
      setSearched(true);
    }
  }, [section, query]);

  const location = useLocation();
  const base =
    location.pathname === "/trade" || location.pathname === "/trade/";

  const touch = matchMedia("(hover: none)").matches;

  return (
    <sort.Provider>
      <div className={!base ? "pr-2" : ""}>
        <Input
          autoFocus={!touch}
          label="Search"
          value={query}
          onChange={(e) => setQuery(e.currentTarget.value)}>
          {query !== "" && (
            <MinusCircle
              className="input-container__clear"
              onClick={() => {
                setQuery("");
                setSearched(false);
              }}
            />
          )}
        </Input>
        <div className={"flex dir-r"}>
          <nav
            className="tabs tabs--sm mt-1 wrap gap-0.5"
            style={{ maxWidth: "100%" }}>
            {sections.map((s) => (
              <a
                key={s}
                className={clsx({
                  selected: section === s,
                  "px-1 lonely": s === Section.Favorites,
                })}
                onClick={() => setSection(s)}>
                {s === Section.Favorites ? (
                  <Icons.StarSolid className="w-2 h-2 ml-0" />
                ) : (
                  s
                )}
              </a>
            ))}
            {!base && (
              <Link to={"/trade"} className={"transparent"}>
                Details
                <Icons.External className="w-2 h-2" />
              </Link>
            )}
          </nav>
        </div>
      </div>
      <div
        className={
          base
            ? "relative card p-3 mt-2 table-sticky"
            : "trade__markets-overflow mt-3"
        }>
        <table
          className={base ? "table table--big condensed" : "table table--va-m"}>
          <thead className="border">
            <tr>
              <th className="nopad w-1"></th>
              <sort.Item label="Pair" value="NAME" />
              <i18n.th className="text-right nowrap">Last Price</i18n.th>
              <sort.Item label="Change" value="CHANGE" className="jc-e" />
              <sort.Item
                label="24h Volume"
                value="VOLUME_USD"
                className="jc-e"
              />
            </tr>
          </thead>
          <tbody>
            <Suspense fallback={<Fallback />}>
              <Content
                closeDrawer={closeDrawer}
                query={query}
                section={section}
              />
            </Suspense>
          </tbody>
        </table>
      </div>
    </sort.Provider>
  );
};

type Node = NonNullable<
  NonNullable<
    NonNullable<NonNullable<MarketsQuery$data>["finV2"]["edges"]>[0]
  >["node"]
>;

const Content: FC<{
  closeDrawer?: () => void;
  query?: string;
  section?: Section;
}> = ({ closeDrawer, query, section }) => {
  const { sortBy, sortDir } = sort.useValues();
  const data = useLazyLoadQuery<MarketsQuery>(
    q,
    { sortBy, sortDir },
    { fetchPolicy: "store-and-network" }
  );

  const { favorites, addFavorite, removeFavorite } = useFavorites();

  const toggleFavorite = (v: string) => {
    if (favorites.includes(v)) {
      removeFavorite(v);
    } else {
      addFavorite(v);
    }
  };

  const pairs: Node[] =
    data.finV2.edges?.reduceRight(
      (a: Node[], v) => (v?.node ? [v.node, ...a] : a),
      []
    ) || [];

  const items = pairs
    .filter(assetPairFilter(query))
    .filter(sectionFilter(favorites, section));
  // .sort((a, b) => {
  //   // Prioritize LIVE status
  //   if (a.deploymentStatus === "LIVE" && b.deploymentStatus !== "LIVE")
  //     return -1;
  //   if (a.deploymentStatus !== "LIVE" && b.deploymentStatus === "LIVE")
  //     return 1;

  //   // If both are the same, sort by symbol
  //   return a.assetBase.metadata.symbol.localeCompare(
  //     b.assetBase.metadata.symbol
  //   );
  // });

  if (!items?.length) {
    return (
      <tr>
        <td colSpan={5} className="text-center color-grey">
          <br />
          {i18n.t("No results found")}
        </td>
      </tr>
    );
  }

  return items.map((x) => (
    <Item
      key={x.address}
      k={x}
      closeDrawer={closeDrawer}
      favorite={favorites.includes(x.id)}
      toggleFavorite={() => toggleFavorite(x.id)}
    />
  ));
};

const fragment = graphql`
  fragment MarketsItemFragment on FinPair {
    id
    address
    deploymentStatus
    assetBase {
      asset
      chain
      type
      metadata {
        symbol
        decimals
      }
    }
    assetQuote {
      asset
      chain
      type
      metadata {
        symbol
        decimals
      }
      price {
        current
      }
    }
    summary {
      last
      change
      volumeUsd
      volume {
        amount
        asset {
          metadata {
            symbol
          }
        }
      }
    }
  }
`;

const Item: FC<{
  k: MarketsItemFragment$key;
  closeDrawer?: () => void;
  toggleFavorite: () => void;
  favorite: boolean;
}> = ({ k, closeDrawer, toggleFavorite, favorite }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const x = useFragment(fragment, k);
  const change = BigInt(x.summary?.change || 0);
  const base =
    location.pathname === "/trade" || location.pathname === "/trade/";

  return (
    <tr
      key={x.address}
      className="pointer"
      onClick={() => {
        navigate(
          `/trade/${toFinAssetId(x.assetBase)}/${toFinAssetId(x.assetQuote)}`
        );
        closeDrawer?.();
      }}>
      <td className="nopad">
        <button
          className="transparent py-1 pr-0.5 color-grey pointer"
          onClick={(e) => {
            e.stopPropagation();
            toggleFavorite();
          }}>
          {favorite ? (
            <Icons.StarSolid className={base ? "w-3 h-3" : "w-2 h-2"} />
          ) : (
            <Icons.Star className={base ? "w-3 h-3" : "w-2 h-2"} />
          )}
        </button>
      </td>
      <td>
        <div
          className={clsx({
            "flex ai-c": true,
            "fs-20 fs-lg-26 fw-400": base,
          })}>
          <div className={base ? "lp mt-0.5" : "pair mr-0.5"}>
            <IconDenom
              className={base ? "" : "w-2"}
              denom={x.assetBase.metadata.symbol}
            />
            <IconDenom
              className={base ? "" : "w-2"}
              denom={x.assetQuote.metadata.symbol}
            />
          </div>
          <div
            className={clsx({
              "flex ai-b": true,
              "ml-1": base,
            })}>
            <AssetLabel
              asset={x.assetBase}
              Container={({ children }) => (
                <span
                  className={clsx({
                    "color-grey": true,
                    "fs-11 fw-500": !base,
                    "fs-14 fs-lg-16": base,
                  })}>
                  {children}
                </span>
              )}
            />

            <span className="color-grey ml-0.5">
              <AssetLabel
                asset={x.assetQuote}
                Container={({ children }) => (
                  <span
                    className={clsx({
                      "color-grey": true,
                      "fs-11 fw-500": !base,
                      "fs-14 fs-lg-16": base,
                    })}>
                    {children}
                  </span>
                )}
              />
            </span>
          </div>
        </div>
      </td>
      {x.deploymentStatus === "LIVE" ? (
        <>
          <td className="text-right">
            <span
              className={clsx({
                "condensed fs-16 fs-lg-18": base,
              })}>
              {priceFormatter(BigInt(x?.summary?.last || 0))}
              <span
                className={clsx("color-grey", {
                  "fs-13 fs-lg-15": base,
                  "fs-12": !base,
                })}>
                {" " + x.assetQuote.metadata.symbol}
              </span>
            </span>
            {base && (
              <>
                <br />
                <Fiat
                  amount={
                    BigInt(x?.summary?.last || 0) *
                    BigInt(x.assetQuote.price?.current || 1)
                  }
                  decimals={24}
                  symbol="$"
                  className="color-grey fs-14 condensed"
                />
              </>
            )}
          </td>
          <td
            className={clsx({
              "text-right": true,
              "color-teal": change > 0n,
              "color-red": change < 0n,
              "color-grey": change === 0n,
            })}>
            {change > 0n ? "+" : ""}
            {(Number(change) / 10 ** 10).toLocaleDecimal(2)}%
          </td>
          <td
            className={clsx({
              "text-right": true,
              "fs-14 fs-lg-15": base,
            })}>
            ${nFormatter(BigInt(x.summary?.volumeUsd || 0), 2, 8)}
            {base && (
              <>
                <br />
                <Decimal
                  amount={BigInt(x.summary?.volume.amount || 0)}
                  decimals={8}
                  symbol={x.summary?.volume.asset.metadata.symbol}
                  className="color-grey fs-14 condensed"
                />
              </>
            )}
          </td>
        </>
      ) : (
        <td colSpan={3} className="text-center">
          <i18n.span className="tag tag--md inline-block">
            Preview only
          </i18n.span>
        </td>
      )}
    </tr>
  );
};

const Fallback = () => (
  <>
    {[...Array(10)].map((_, i) => (
      <tr key={`skeleton_${i}`}>
        <td className="nopad w-1"></td>
        <td className={`opacity-${20 - i * 2}`}>
          <div className="flex ai-c">
            <div className="pair mr-0.5">
              <div className="icon-denom skeleton h-2 w-2 br-2" />
              <div className="icon-denom skeleton h-2 w-2 br-2" />
            </div>
            <div className="w-full skeleton h-1.5 w-1.5 br-2" />
          </div>
        </td>
        <td className={`opacity-${20 - i * 2}`}>
          <div className="w-full skeleton h-1.5 w-1.5 br-2" />
        </td>
        <td className={`opacity-${20 - i * 2}`}>
          <div className="w-full skeleton h-1.5 w-1.5 br-2" />
        </td>
        <td className={`opacity-${20 - i * 2}`}>
          <div className="w-full skeleton h-1.5 w-1.5 br-2" />
        </td>
      </tr>
    ))}
  </>
);
